import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { AiOutlineReload } from 'react-icons/ai';
import { FiEdit, FiEye, FiCalendar, FiLayers, FiGlobe, FiXCircle, FiCircle, FiFileText, FiClock } from 'react-icons/fi';
import { FaCircle } from 'react-icons/fa';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";

const Forms = () => {
    const [loading, setLoading] = useState(true);
    const [forms, setForms] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { axiosInstance, activeOrganization } = useAuth();

    // Helper functions for form URL
    const getFormBaseUrl = () => {
        return import.meta.env.VITE_FORMS_FRONTEND_BASE_URL || 'https://forms.status.bot';
    };
    
    const getFormDisplayDomain = () => {
        const baseUrl = getFormBaseUrl();
        return baseUrl.replace(/^https?:\/\//, '');
    };

    const fetchForms = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/forms');
            setForms(response.data);
        } catch (error) {
            console.error('Error fetching forms:', error);
            toast.error('Error fetching forms, please try again later');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchForms();
    }, []);

    const handleRowClick = (form) => {
        navigate(`/${form.organizationSlug}/forms/edit/${form.slug}`);
    };

    const handleViewSubmissions = (e, form) => {
        e.stopPropagation(); // Prevent row click event
        navigate(`/${form.organizationSlug}/forms/${form.slug}/submissions`);
    };

    return (
        <Layout>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-8"
            >
                <Card>
                    <CardHeader className="flex flex-row justify-between">
                        <CardTitle className="text-3xl font-bold">Forms</CardTitle>
                        <div className="flex space-x-4">
                            <Button variant="ghost" size="icon" onClick={() => fetchForms()}>
                                <AiOutlineReload className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button onClick={() => navigate(`/${activeOrganization?.slug}/forms/create`)}>
                                Create Form
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[...Array(6)].map((_, i) => (
                                    <Card key={i}>
                                        <CardContent className="p-4">
                                            <div className="space-y-3">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0 space-y-1">
                                                        <Skeleton className="h-4 w-3/4" />
                                                        <Skeleton className="h-3 w-1/2" />
                                                    </div>
                                                    <Skeleton className="h-4 w-16" />
                                                </div>
                                                
                                                <Skeleton className="h-4 w-2/3" />
                                                
                                                <div className="flex justify-between">
                                                    <Skeleton className="h-3 w-20" />
                                                    <Skeleton className="h-3 w-16" />
                                                </div>
                                                
                                                <div className="flex space-x-2">
                                                    <Skeleton className="h-8 flex-1" />
                                                    <Skeleton className="h-8 flex-1" />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : forms.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {forms.map((form) => (
                                    <motion.div
                                        key={form._id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <Card 
                                            className="cursor-pointer transition-all duration-200 hover:shadow-md group relative"
                                            onClick={() => handleRowClick(form)}
                                        >
                                            <CardContent className="p-4">
                                                <div className="space-y-3">
                                                    {/* Header with name and status */}
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="text-lg font-semibold text-foreground truncate group-hover:text-violet-600 dark:group-hover:text-violet-500 transition-colors">
                                                                {form.name}
                                                            </h3>
                                                            <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                                                                {getFormDisplayDomain()}/{form.slug}
                                                            </p>
                                                        </div>
                                                        <div className="flex items-center ml-3">
                                                            {form.online ? (
                                                                <Badge variant="outline" className="border-emerald-200 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300 text-xs px-3 py-1 flex items-center space-x-1">
                                                                    <FaCircle className="h-2 w-2" />
                                                                    <span>Online</span>
                                                                </Badge>
                                                            ) : (
                                                                <Badge variant="outline" className="border-muted-foreground/30 text-muted-foreground text-xs px-3 py-1 flex items-center space-x-1">
                                                                    <FiCircle className="h-2 w-2" />
                                                                    <span>Offline</span>
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Organization */}
                                                    <div className="flex items-center text-sm">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mr-2"></div>
                                                        <span className="text-muted-foreground">{form.organizationName}</span>
                                                    </div>

                                                    {/* Meta information */}
                                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                        <div className="flex items-center space-x-1">
                                                            <FiClock className="h-3 w-3" />
                                                            <span>
                                                                {form.lastSubmissionDate 
                                                                    ? `Last: ${new Date(form.lastSubmissionDate).toLocaleDateString()}`
                                                                    : 'No submissions yet'
                                                                }
                                                            </span>
                                                        </div>
                                                        {(form.submissionCount || 0) > 0 && (
                                                            <div className="flex items-center space-x-1">
                                                                <FiFileText className="h-3 w-3" />
                                                                <span>{form.submissionCount} submission{form.submissionCount !== 1 ? 's' : ''}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex space-x-2 pt-2 border-t border-border">
                                                        <Button 
                                                            variant="outline" 
                                                            size="sm"
                                                            onClick={(e) => handleViewSubmissions(e, form)}
                                                            className="flex-1 flex items-center justify-center space-x-1 text-xs h-7"
                                                        >
                                                            <FiEye className="h-3 w-3" />
                                                            <span>View Submissions</span>
                                                        </Button>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigate(`/${form.organizationSlug}/forms/edit/${form.slug}`);
                                                            }}
                                                            className="flex-1 flex items-center justify-center space-x-1 text-xs h-7"
                                                        >
                                                            <FiEdit className="h-3 w-3" />
                                                            <span>Edit</span>
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <div className="mx-auto h-32 w-32 bg-muted/50 rounded-full flex items-center justify-center mb-6">
                                    <FiLayers className="h-16 w-16 text-muted-foreground" />
                                </div>
                                <h3 className="text-2xl font-semibold text-foreground mb-3">No forms found</h3>
                                <p className="text-muted-foreground mb-8 max-w-md mx-auto">Get started by creating your first form to begin collecting valuable insights from your users.</p>
                                <Button 
                                    onClick={() => navigate(`/${activeOrganization?.slug}/forms/create`)}
                                    className="px-8 py-3 text-lg font-semibold"
                                >
                                    Create Your First Form
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </motion.div>
        </Layout>
    );
};

export default Forms;
